import React from 'react';
import {
    AppstoreAddOutlined, DownloadOutlined, EyeOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import {Tag, Tooltip} from 'antd';
import {Link, useNavigate} from 'react-router-dom';
import {convertBytes, downloadBase64File, getFileIcon} from "../../../utils/file.utils";
import {upperCaseFirstLetter} from "../../../utils/string.utils";
import {ProcessingStatusColorsEnum, AttachmentProcessingStatusColorsEnum} from "../../../constants/status.constants.js"


const EmailAttachmentCard = ({
    attachment = null,
    documentId = null,
    attachmentStatus = null,
    documentProcessingStatus = null,
    comment = null,
    invoiceMatchConfidence = null,
    isAssignSupplierVisible = false,
    onClickHandleAssignPopup
}) => {
    const navigate = useNavigate();

    if (!attachment) return null;

    return (
        <div className="flex flex-col gap-2 px-3 py-2.5 border rounded bg-[#F0F4F6]/50 hover:bg-gray-100 transition-colors">
            {/* Header row: icon + name + size + action icons */}
            <div className="flex items-center gap-2">
                {getFileIcon(attachment.fileName)}

                <Tooltip title={attachment.fileName} placement="top">
                    <span className="text-xs flex-1 truncate min-w-0 text-gray-700">
                        {attachment.fileName}
                    </span>
                </Tooltip>

                <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                    {convertBytes(attachment.size)} KB
                </span>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <Tooltip title="Download" placement="top">
                        <DownloadOutlined
                            className="cursor-pointer text-gray-500 hover:text-gray-800 text-sm"
                            onClick={() => downloadBase64File({
                                content: attachment.content,
                                fileName: attachment.fileName,
                                type: attachment.type,
                            })}
                        />
                    </Tooltip>
                    <Tooltip title={documentId ? "View Invoice" : "Invoice Not Found"} placement="top">
                        <EyeOutlined
                            className={`text-sm ${documentId ? "cursor-pointer text-gray-500 hover:text-gray-800" : "text-gray-300 cursor-not-allowed"}`}
                            onClick={() => documentId && navigate(`/details/${documentId}`)}
                        />
                    </Tooltip>
                    <Tooltip title="Assign Supplier" placement="top">
                        <AppstoreAddOutlined
                            className={`text-sm ${isAssignSupplierVisible ? "cursor-pointer text-gray-500 hover:text-gray-800" : "text-gray-300 cursor-not-allowed"}`}
                            onClick={() => isAssignSupplierVisible && onClickHandleAssignPopup()}
                        />
                    </Tooltip>
                </div>
            </div>

            {/* Status tags + invoice link row */}
            {(attachmentStatus || documentProcessingStatus || invoiceMatchConfidence > 0 || documentId) && (
                <div className="flex items-center flex-wrap gap-1.5">
                    {attachmentStatus && (
                        <Tooltip title="Attachment Status" placement="top">
                            <Tag color={AttachmentProcessingStatusColorsEnum[attachmentStatus]} className="!m-0 text-[10px]">
                                {upperCaseFirstLetter(attachmentStatus)}
                            </Tag>
                        </Tooltip>
                    )}
                    {documentProcessingStatus && (
                        <Tooltip title="Document Processing Status" placement="top">
                            <Tag color={ProcessingStatusColorsEnum[documentProcessingStatus]} className="!m-0 text-[10px]">
                                {upperCaseFirstLetter(documentProcessingStatus)}
                            </Tag>
                        </Tooltip>
                    )}
                    {invoiceMatchConfidence > 0 && (
                        <Tooltip title="Document Confidence" placement="top">
                            <Tag
                                color={invoiceMatchConfidence >= 80 ? "red" : invoiceMatchConfidence >= 50 ? "gold" : "red"}
                                className="!m-0 text-[10px]"
                            >
                                {Math.round(invoiceMatchConfidence)}% Duplicate Shipsure Match
                            </Tag>
                        </Tooltip>
                    )}
                    {documentId && (
                        <Link
                            to={`/details/${documentId}`}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline ml-auto"
                        >
                            <FileSearchOutlined className="text-xs"/>
                            View Invoice
                        </Link>
                    )}
                </div>
            )}

            {/* Comment row */}
            {comment && (
                <div className="text-[11px] text-gray-600 italic border-l-2 border-gray-300 pl-2 leading-snug">
                    {comment}
                </div>
            )}
        </div>
    );
};

export default EmailAttachmentCard;
